<script setup lang="ts">
import { FormKitLazyProvider } from '@formkit/vue'
// Overrides @basecom-gmbh/pond/components/Shared/SharedModal.vue
// IDS-130 add "closable" configuration to enable not closable modals
// IDS-135 add ids layout (on mobile full width, some padding and other smaller stylings), move close button
const props = withDefaults(
    defineProps<{
        controller?: ReturnType<typeof useModal>;
        withCloseButton?: boolean;
        withActionsButton?: boolean;
        size?: 'sm' | 'md' | 'lg';
        closable?: boolean;
        withTrigger?: boolean;
    }>(),
    {
        controller: useModal,
        withCloseButton: false,
        withActionsButton: false,
        size: 'md',
        closable: true,
        withTrigger: true,
    },
);
const { controller } = toRefs(props);

const onUpdatedOpen = (value: boolean) => {
    if (!props.closable) return;

    if (value) {
        controller.value.open();
    } else {
        controller.value.close();
    }
};

watch(controller.value.isOpen, isOpen => {
    if (!isOpen) {
        setTimeout(() => {
            document.body.style.overflow = 'unset';
        }, 1);
    }
});
</script>

<template>
    <DialogRoot
        v-model:open="controller.isOpen.value"
        :model="true"
        @update:open="onUpdatedOpen"
    >
        
<FormKitLazyProvider config-file="true">
<DialogTrigger v-if="withTrigger">
            <slot name="trigger" />
        </DialogTrigger>
        <DialogPortal>
            <Transition
                enter-active-class="transition ease-out duration-200 transform"
                enter-from-class="opacity-0"
                enter-to-class="opacity-100"
                leave-active-class="transition ease-in duration-200 transform"
                leave-from-class="opacity-100"
                leave-to-class="opacity-0"
            >
                <DialogOverlay class="data-[state=open]:animate-overlayShow bg-black/50 fixed inset-0 z-30" />
            </Transition>
            <Transition
                enter-active-class="transition ease-out duration-300 transform "
                enter-from-class="-translate-y-10 scale-50"
                enter-to-class="translate-y-0 scale-100"
                leave-active-class="ease-in duration-300"
                leave-from-class="translate-y-0 scale-100"
                leave-to-class="-translate-y-10 scale-50"
            >
                <DialogContent
                    class="bg-white fixed left-1/2 top-0 z-[100] max-h-[90vh] w-full -translate-x-1/2 overflow-auto overflow-y-auto shadow-lg focus:outline-none md:top-8 md:max-h-[calc(100vh-4rem)]"
                    :class="{
                        'md:max-w-[min(calc(100%-2.5rem),20rem)]': size === 'sm',
                        'md:max-w-[min(calc(100%-2.5rem),45.75rem)]': size === 'md',
                        'md:max-w-[min(calc(100%-2.5rem),66.66%)]': size === 'lg',
                    }"
                >
                    <DialogTitle
                        class="flex items-center justify-between gap-4 p-6 font-extrabold md:px-10 md:py-8 md:pb-6"
                    >
                        <slot name="title" />

                        <DialogClose
                            v-if="withCloseButton"
                            class="hover:text-black focus:text-black h-6 w-6 items-center justify-center text-neutral-800"
                            :aria-label="$t('shared.modal.closeButtonAriaLabel')"
                        >
                            <FormKitIcon
                                class="block"
                                icon="x"
                            />
                        </DialogClose>
                    </DialogTitle>
                    <div class="p-6 pt-0 md:px-10 md:py-8 md:pt-0">
                        <DialogDescription>
                            <slot name="description" />
                        </DialogDescription>
                        <slot name="content" />
                    </div>
                    <div
                        v-if="withActionsButton"
                        class="flex justify-between border-t border-gray-light px-6 pb-6 pt-4"
                    >
                        <slot name="actions">
                            <DialogClose as-child>
                                <FormKit
                                    type="button"
                                    :label="$t('shared.modal.cancelButtonLabel')"
                                    :classes="{
                                        outer: 'w-full',
                                    }"
                                />
                            </DialogClose>
                        </slot>
                    </div>
                </DialogContent>
            </Transition>
        </DialogPortal>
</FormKitLazyProvider>

    </DialogRoot>
</template>
